import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "../components/ui/dialog";
import { Plus, Activity, Edit2, Trash2, FileText, Search } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", symptoms: "", description: "" };

export default function Diseases() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const PAGE = 12;

  const load = useCallback(
    (search = "", size = PAGE) =>
      api
        .get("/diseases", { params: { ...(search ? { q: search } : {}), limit: size } })
        .then((r) => {
          setItems(r.data.items || []);
          setTotal(r.data.total || 0);
        })
        .catch((err) => console.error("Failed to load diseases", err)),
    []
  );
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q, load]);

  const openNew = () => {
    setForm(empty);
    setEditingId(null);
    setOpen(true);
  };
  const openEdit = (d) => {
    setForm({ name: d.name || "", symptoms: d.symptoms || "", description: d.description || "" });
    setEditingId(d.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/diseases/${editingId}`, form);
        toast.success("Updated");
      } else {
        await api.post("/diseases", form);
        toast.success("Disease added");
      }
      setOpen(false);
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this disease? Tagged blogs will remain but lose their tag.")) return;
    await api.delete(`/diseases/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6 fade-up">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-secondary font-bold">
            Library
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">
            Know Your Diseases
          </h1>
          <p className="text-muted-foreground mt-2">
            Catalog diseases and tag in-depth blog posts to each.
          </p>
        </div>
        <Button onClick={openNew} data-testid="add-disease-btn" size="lg">
          <Plus className="size-4 mr-2" /> Add Disease
        </Button>
      </header>

      <div className="relative">
        <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search diseases…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9 h-11"
          data-testid="disease-search"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((d) => (
          <Card key={d.id} className="p-5 flex flex-col" data-testid={`disease-card-${d.id}`}>
            <div className="flex items-start gap-3 mb-3">
              <div className="size-10 rounded-md bg-secondary/15 text-secondary-foreground grid place-items-center">
                <Activity className="size-5" strokeWidth={1.6} />
              </div>
              <p className="font-heading text-lg font-semibold flex-1">{d.name}</p>
            </div>
            {d.symptoms && (
              <div className="mb-2">
                <p className="text-[10px] uppercase tracking-widest text-secondary font-bold">
                  Symptoms
                </p>
                <p className="text-sm text-foreground/80 line-clamp-2">{d.symptoms}</p>
              </div>
            )}
            {d.description && (
              <div className="mb-3">
                <p className="text-[10px] uppercase tracking-widest text-secondary font-bold">
                  About
                </p>
                <p className="text-sm text-foreground/80 line-clamp-3">{d.description}</p>
              </div>
            )}
            <div className="flex gap-2 mt-auto pt-3 border-t border-border flex-wrap">
              <Link to={`/diseases/${d.id}`} className="flex-1">
                <Button
                  variant="default"
                  size="sm"
                  className="w-full"
                  data-testid={`view-disease-${d.id}`}
                >
                  <FileText className="size-3.5 mr-1" /> Blogs
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openEdit(d)}
                data-testid={`edit-disease-${d.id}`}
              >
                <Edit2 className="size-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => remove(d.id)}
                className="text-destructive hover:text-destructive"
                data-testid={`delete-disease-${d.id}`}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="col-span-full p-10 text-center border-dashed">
            <p className="text-muted-foreground">No diseases yet.</p>
          </Card>
        )}
      </div>

      {items.length < total && (
        <div className="text-center">
          <Button
            variant="outline"
            onClick={() => load(q, items.length + PAGE)}
            data-testid="load-more-diseases"
          >
            Load more ({total - items.length} remaining)
          </Button>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingId ? "Edit disease" : "New disease"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="disease-form-name"
              />
            </div>
            <div>
              <Label>Symptoms</Label>
              <Textarea
                rows={3}
                value={form.symptoms}
                onChange={(e) => setForm({ ...form, symptoms: e.target.value })}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="disease-form-submit">
                {editingId ? "Update" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
